import React, { useState, useEffect } from "react";
import { authFetch, apiUrl } from "../utils/api";
import "./ROIDashboard.css";

const ROIDashboard = ({ onClose }) => {
  const [stats, setStats] = useState(null);
  const [lifetimeStats, setLifetimeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");
  const [hourlyRate, setHourlyRate] = useState(150);
  const [isEditingRate, setIsEditingRate] = useState(false);
  const [animatedValues, setAnimatedValues] = useState({
    hoursSaved: 0,
    valueDelivered: 0,
    roi: 0,
    tasks: 0,
    lifetimeValue: 0,
    lifetimeTasks: 0,
    daysUsing: 0,
  });

  useEffect(() => {
    loadStats();
    loadLifetimeStats();
    loadHourlyRate();
  }, [period]);

  useEffect(() => {
    if (stats && stats.summary) {
      // Animate numbers with safe fallbacks
      animateValue("hoursSaved", 0, stats.summary.hoursSaved || 0, 2000);
      animateValue(
        "valueDelivered",
        0,
        stats.summary.valueDelivered || 0,
        2000,
      );
      animateValue("roi", 0, stats.summary.roi || 0, 2000);
      animateValue("tasks", 0, stats.summary.totalTasks || 0, 1500);
    }
  }, [stats]);

  useEffect(() => {
    if (lifetimeStats) {
      // Animate lifetime numbers
      animateValue("lifetimeValue", 0, lifetimeStats.valueDelivered || 0, 2500);
      animateValue("lifetimeTasks", 0, lifetimeStats.totalTasks || 0, 2000);
      // Calculate days using from firstActivity if available
      const daysUsing = lifetimeStats.firstActivity
        ? Math.ceil(
            (Date.now() - new Date(lifetimeStats.firstActivity).getTime()) /
              (1000 * 60 * 60 * 24),
          )
        : 0;
      animateValue("daysUsing", 0, daysUsing, 1500);
    }
  }, [lifetimeStats]);

  const animateValue = (key, start, end, duration) => {
    const startTime = Date.now();
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const value = start + (end - start) * easeOutCubic(progress);

      setAnimatedValues((prev) => ({ ...prev, [key]: value }));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    animate();
  };

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        apiUrl(`/api/roi/stats?period=${period}`),
      );
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error("Failed to load ROI stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadLifetimeStats = async () => {
    try {
      const response = await authFetch(apiUrl("/api/roi/snapshot"));
      const data = await response.json();
      setLifetimeStats(data);
    } catch (error) {
      console.error("Failed to load lifetime stats:", error);
    }
  };

  const loadHourlyRate = async () => {
    try {
      const response = await authFetch(apiUrl("/api/roi/settings"));
      const data = await response.json();
      if (data.hourly_rate) {
        setHourlyRate(data.hourly_rate);
      }
    } catch (error) {
      console.error("Failed to load hourly rate:", error);
    }
  };

  const saveHourlyRate = async () => {
    try {
      await authFetch(apiUrl("/api/roi/settings"), {
        method: "POST",
        body: JSON.stringify({ hourly_rate: hourlyRate }),
      });
      setIsEditingRate(false);
      // Reload both stats to recalculate values with new hourly rate
      await Promise.all([loadStats(), loadLifetimeStats()]);
    } catch (error) {
      console.error("Failed to save hourly rate:", error);
    }
  };

  const exportReport = async (format) => {
    try {
      const response = await authFetch(
        apiUrl(`/api/roi/export?format=${format}&period=${period}`),
      );

      if (format === "csv") {
        // CSV returns raw text, not JSON
        const csvText = await response.text();
        const blob = new Blob([csvText], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `optimus-roi-report-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // JSON format
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data.data, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `optimus-roi-report-${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Failed to export report:", error);
    }
  };

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    // Sub-$10 values show cents so $0.50 doesn't round to $1.
    // $10+ values stay clean integers for big-number readability.
    const fractionDigits = Math.abs(num) < 10 ? 2 : 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(num);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(Math.round(value));
  };

  if (loading) {
    return (
      <div className="roi-dashboard-overlay">
        <div className="roi-dashboard">
          <div className="roi-loading">
            <div className="roi-loading-spinner"></div>
            <p>Calculating your ROI...</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has no data yet (new user)
  const hasNoData =
    !stats?.summary?.totalTasks &&
    !lifetimeStats?.totalTasks &&
    animatedValues.tasks === 0;

  // Show welcome state for new users
  if (hasNoData) {
    return (
      <div className="roi-dashboard-overlay" onClick={onClose}>
        <div className="roi-dashboard" onClick={(e) => e.stopPropagation()}>
          <div className="roi-header">
            <div className="roi-title-section">
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <img
                  src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
                  alt="Optimus OS"
                  style={{ height: "32px" }}
                />
                <h2 className="roi-title">💰 ROI Dashboard</h2>
              </div>
            </div>
            <button className="roi-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
          <div
            className="hf-reveal"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 40px",
              textAlign: "center",
              minHeight: "400px",
            }}
          >
            <div style={{ fontSize: "64px", marginBottom: "24px" }}>📊</div>
            <h2
              style={{ fontSize: "28px", marginBottom: "16px", color: "var(--text-primary)" }}
            >
              Your ROI Story Starts Here
            </h2>
            <p
              style={{
                fontSize: "16px",
                color: "var(--text-secondary)",
                maxWidth: "500px",
                marginBottom: "32px",
                lineHeight: "1.6",
              }}
            >
              As you use Optimus to automate tasks, we'll track the{" "}
              <strong style={{ color: "var(--accent-primary)" }}>time saved</strong> and
              <strong style={{ color: "var(--accent-primary)" }}> value created</strong> so
              you can see your real ROI.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "24px",
                marginBottom: "40px",
                width: "100%",
                maxWidth: "600px",
              }}
            >
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  padding: "20px",
                  borderRadius: "12px",
                }}
              >
                <div style={{ fontSize: "32px", marginBottom: "8px" }}>⏱️</div>
                <div style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
                  Hours Saved
                </div>
              </div>
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  padding: "20px",
                  borderRadius: "12px",
                }}
              >
                <div style={{ fontSize: "32px", marginBottom: "8px" }}>💵</div>
                <div style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
                  Value Created
                </div>
              </div>
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  padding: "20px",
                  borderRadius: "12px",
                }}
              >
                <div style={{ fontSize: "32px", marginBottom: "8px" }}>🚀</div>
                <div style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
                  Tasks Automated
                </div>
              </div>
            </div>
            <p style={{ fontSize: "14px", color: "var(--text-tertiary)" }}>
              💡 Try asking Ollie: "Find 10 leads in Austin" or "Create a weekly
              report automation"
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="roi-dashboard-overlay" onClick={onClose}>
      <div className="roi-dashboard" onClick={(e) => e.stopPropagation()}>
        <div className="roi-header">
          <div className="roi-title-section">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <img
                src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
                alt="Optimus OS"
                style={{ height: "32px" }}
              />
              <h2 className="roi-title">💰 ROI Dashboard</h2>
            </div>
            <p className="roi-subtitle">Your Value Creation Command Center</p>
          </div>

          <div className="roi-controls">
            <div className="roi-period-selector">
              <button
                className={period === "today" ? "active" : ""}
                onClick={() => setPeriod("today")}
              >
                Today
              </button>
              <button
                className={period === "week" ? "active" : ""}
                onClick={() => setPeriod("week")}
              >
                Week
              </button>
              <button
                className={period === "month" ? "active" : ""}
                onClick={() => setPeriod("month")}
              >
                Month
              </button>
              <button
                className={period === "all" ? "active" : ""}
                onClick={() => setPeriod("all")}
              >
                All Time
              </button>
            </div>

            <div className="hourly-rate-input">
              <span className="hourly-rate-label">Your Hourly Rate:</span>
              {isEditingRate ? (
                <>
                  <input
                    type="number"
                    min="0"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value))}
                    className="hourly-rate-field"
                  />
                  <button
                    onClick={saveHourlyRate}
                    className="hourly-rate-btn hourly-rate-btn-primary"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingRate(false);
                      loadHourlyRate();
                    }}
                    className="hourly-rate-btn hourly-rate-btn-secondary"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="hourly-rate-value">
                    ${hourlyRate || 0}/hr
                  </span>
                  <button
                    onClick={() => setIsEditingRate(true)}
                    className="hourly-rate-btn hourly-rate-btn-edit"
                  >
                    Edit
                  </button>
                </>
              )}
            </div>

            <button className="roi-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* LIFETIME ACHIEVEMENT SECTION */}
        <div className="lifetime-section">
          <h3>🏆 LIFETIME ACHIEVEMENTS</h3>
          <div className="lifetime-grid hf-stagger">
            <div className="lifetime-card gold hf-panel hf-lift">
              <div className="lifetime-label">All-Time Value Created</div>
              <div className="lifetime-value hf-count">
                {formatCurrency(animatedValues.lifetimeValue)}
              </div>
              <div className="lifetime-context">
                Since{" "}
                {lifetimeStats?.created_at
                  ? new Date(lifetimeStats.created_at).toLocaleDateString()
                  : "the beginning"}
              </div>
            </div>

            <div className="lifetime-card hf-panel hf-lift">
              <div className="lifetime-label">Total Tasks Completed</div>
              <div className="lifetime-value hf-count">
                {formatNumber(animatedValues.lifetimeTasks)}
              </div>
            </div>

            <div className="lifetime-card hf-panel hf-lift">
              <div className="lifetime-label">Days Using Optimus</div>
              <div className="lifetime-value hf-count">
                {formatNumber(animatedValues.daysUsing)}
              </div>
            </div>

            <div className="lifetime-card hf-panel hf-lift">
              <div className="lifetime-label">Lifetime ROI</div>
              <div className="lifetime-value hf-count">
                {lifetimeStats?.lifetime_roi == null
                  ? "—"
                  : `${Number(lifetimeStats.lifetime_roi).toFixed(0)}x`}
              </div>
            </div>
          </div>

          {/* Milestones */}
          {lifetimeStats?.milestones && lifetimeStats.milestones.length > 0 && (
            <div className="milestones">
              {lifetimeStats.milestones.slice(0, 5).map((m, index) => {
                const getMilestoneMessage = (type) => {
                  const messages = {
                    first_dollar: "🎉 First Dollar Saved!",
                    first_hundred: "💯 $100 Milestone!",
                    first_thousand: "🎯 $1,000 Saved!",
                    ten_thousand: "🚀 $10,000 Saved!",
                    hundred_thousand: "🤯 $100,000 Saved!",
                    million_dollar: "👑 MILLION DOLLAR MILESTONE!",
                  };
                  return messages[type] || `💰 $${m.milestone_value} Saved!`;
                };

                return (
                  <div
                    key={m.milestone_type || index}
                    className="milestone-badge"
                  >
                    {getMilestoneMessage(m.milestone_type)}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="roi-hero-metrics hf-stagger">
          <div className="roi-metric-card roi-metric-primary hf-panel hf-lift">
            <div className="roi-metric-icon">🚀</div>
            <div className="roi-metric-value hf-count">
              {animatedValues.roi.toFixed(1)}%
            </div>
            <div className="roi-metric-label">ROI ({period})</div>
            <div className="roi-metric-sublabel">Return on Investment</div>
          </div>

          <div className="roi-metric-card hf-panel hf-lift">
            <div className="roi-metric-icon">💵</div>
            <div className="roi-metric-value hf-count">
              {formatCurrency(animatedValues.valueDelivered)}
            </div>
            <div className="roi-metric-label">Value Delivered</div>
            <div className="roi-metric-sublabel">
              Cost: {formatCurrency(stats?.summary?.aiCost || 0)}
            </div>
          </div>

          <div className="roi-metric-card hf-panel hf-lift">
            <div className="roi-metric-icon">⏱️</div>
            <div className="roi-metric-value hf-count">
              {animatedValues.hoursSaved.toFixed(1)}h
            </div>
            <div className="roi-metric-label">Time Saved</div>
            <div className="roi-metric-sublabel">
              ~{formatCurrency(animatedValues.hoursSaved * hourlyRate)} value
            </div>
          </div>

          <div className="roi-metric-card hf-panel hf-lift">
            <div className="roi-metric-icon">✅</div>
            <div className="roi-metric-value hf-count">
              {formatNumber(animatedValues.tasks)}
            </div>
            <div className="roi-metric-label">Tasks Completed</div>
            <div className="roi-metric-sublabel">
              {stats?.summary?.totalTasks > 0
                ? `${(stats.summary.avgHoursPerTask || 0).toFixed(1)}h avg/task`
                : "—"}
            </div>
          </div>
        </div>

        {stats?.timeSeries && stats.timeSeries.length > 0 && (
          <div className="roi-chart-section">
            <h3>Value Trend</h3>
            <div className="roi-chart">
              <div className="roi-chart-bars">
                {stats.timeSeries.slice(-7).map((day, index) => {
                  const maxValue = Math.max(
                    ...stats.timeSeries.map((d) => d.value),
                    0,
                  );
                  // Guard against all-zero series (NaN heights, missing bars)
                  const height = maxValue > 0 ? (day.value / maxValue) * 100 : 0;
                  return (
                    <div key={index} className="roi-chart-bar-container">
                      <div
                        className="roi-chart-bar"
                        style={{
                          height: `${height}%`,
                          animationDelay: `${index * 100}ms`,
                        }}
                      >
                        <span className="roi-chart-value">
                          ${Math.round(day.value)}
                        </span>
                      </div>
                      <span className="roi-chart-label">
                        {new Date(day.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="roi-breakdown-section">
          {stats?.categories && stats.categories.length > 0 && (
            <div className="roi-breakdown-card">
              <h3>Value by Category</h3>
              <div className="roi-category-list">
                {stats.categories.map((cat, index) => (
                  <div key={index} className="roi-category-item">
                    <div className="roi-category-info">
                      <span className="roi-category-name">
                        {cat.task_category}
                      </span>
                      <span className="roi-category-count">
                        {cat.count} tasks
                      </span>
                    </div>
                    <div className="roi-category-value">
                      {formatCurrency(cat.value)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats?.providers && stats.providers.length > 0 && (
            <div className="roi-breakdown-card">
              <h3>AI Provider Performance</h3>
              <div className="roi-provider-list">
                {stats.providers.map((provider, index) => (
                  <div key={index} className="roi-provider-item">
                    <div className="roi-provider-info">
                      <span className="roi-provider-name">
                        {provider.provider === "claude"
                          ? "🤖 Claude"
                          : provider.provider === "gemini"
                            ? "✨ Gemini"
                            : provider.provider === "openai"
                              ? "🧠 OpenAI"
                              : provider.provider}
                      </span>
                      <span className="roi-provider-stats">
                        {provider.calls} calls • {formatNumber(provider.tokens)}{" "}
                        tokens
                      </span>
                    </div>
                    <div className="roi-provider-metrics">
                      <span className="roi-provider-value">
                        {formatCurrency(provider.value)}
                      </span>
                      <span className="roi-provider-cost">
                        Cost: {formatCurrency(provider.cost)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="roi-actions">
          <button
            className="roi-export-btn"
            onClick={() => exportReport("csv")}
          >
            📊 Export CSV Report
          </button>
          <button
            className="roi-export-btn"
            onClick={() => exportReport("json")}
          >
            📄 Export JSON Data
          </button>
        </div>

        <div className="roi-footer">
          <p className="roi-footer-text">
            💡 Tip: Show this dashboard to your CFO for instant approval on
            Optimus upgrades!
          </p>
        </div>
      </div>
    </div>
  );
};

export default ROIDashboard;
